import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { PlusIcon } from "@heroicons/react/24/outline";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Modal from "./components/ui/Modal";
import Table from "./components/ui/Table";
import Pagination from "./components/ui/Pagination";
import Spinner from "./components/ui/Spinner";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const GamePlan = () => {
  const [gamePlans, setGamePlans] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [gameFilter, setGameFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 30;
  const [formData, setFormData] = useState({
    gameId: "",
    name: "",
    coin: "",
    receiverCoin: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchGamePlans();
    fetchGames();
  }, []);

  // Handle tab change
  const handleTabChange = (tabId, gameId = null) => {
    setActiveTab(tabId);
    setGameFilter(gameId);
    setCurrentPage(1);
    fetchGamePlans(gameId, 1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchGamePlans(gameFilter, newPage);
    }
  };

  // Fetch games for dropdown
  const fetchGames = async () => {
    try {
      const res = await fetch(`${baseUrl}/game/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status && result.data) {
        setGames(result.data);
      } else {
        console.error("Failed to fetch games:", result.message);
      }
    } catch (err) {
      console.error("Error fetching games:", err.message);
    }
  };

  // Fetch game plans
  const fetchGamePlans = async (gameId = null, page = 1) => {
    setLoading(true);
    try {
      let url = `${baseUrl}/game-plan/list?page=${page}&limit=${itemsPerPage}`;
      if (gameId) {
        url += `&gameId=${gameId}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch game plans');
      }

      const result = await res.json();

      if (result.status) {
        // Handle the API's pagination structure
        const pagination = result.pagination || {};
        const items = result.data || [];

        setGamePlans(items);
        setTotalPages(pagination.lastPage || 1);
        setTotalItems(pagination.total || items.length);
        setCurrentPage(pagination.currentPage || 1);
      } else {
        throw new Error(result.message || "Failed to fetch game plans");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" }); // clear error
  };

  // Validation
  const validateForm = () => {
    let errors = {};

    if (!formData.gameId) {
      errors.gameId = "Please select a game";
    }
    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters";
    }
    if (!formData.coin || isNaN(formData.coin) || Number(formData.coin) <= 0) {
      errors.coin = "Coin must be a positive number";
    }
    if (!formData.receiverCoin || isNaN(formData.receiverCoin) || Number(formData.receiverCoin) <= 0) {
      errors.receiverCoin = "Receiver Coin must be a positive number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Normalize game plan response
  const normalizeGamePlan = (res) => {
    return res?.data?.gamePlan || res?.data || res?.gamePlan || res;
  };

  const resetForm = () =>
    setFormData({ gameId: "", name: "", coin: "", receiverCoin: "" });

  // Add new game plan
  const handleAddNew = async () => {
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${baseUrl}/game-plan/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Create failed");

      const newGamePlan = normalizeGamePlan(result);
      setGamePlans((prev) => [...prev, newGamePlan]);
      resetForm();
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game plan created successfully!");
      await fetchGamePlans();
    } catch (error) {
      toast.error("Game plan creation failed!");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit click
  const handleEdit = (index) => {
    setEditingIndex(index);
    const plan = gamePlans[index];
    setFormData({
      gameId: plan.gameId || "",
      name: plan.name || "",
      coin: plan.coin || "",
      receiverCoin: plan.receiverCoin || "",
    });
    setShowModal(true);
  };

  // Save edited game plan
  const handleSave = async () => {
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    const current = gamePlans[editingIndex];
    const planId = current._id || current.id;

    try {
      const res = await fetch(`${baseUrl}/game-plan/edit/${planId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      await fetchGamePlans();

      resetForm();
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game plan updated successfully!");
    } catch (error) {
      toast.error("Game plan update failed!");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this game plan?")) return;
    try {
      const res = await fetch(`${baseUrl}/game-plan/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Delete failed");

      setGamePlans((prev) => prev.filter((g) => g.id !== id && g._id !== id));
      toast.success("Game plan deleted successfully!");
    } catch (error) {
      toast.error("Game plan deletion failed!");
    }
  };

  // Toggle active/inactive
  const toggleActive = async (id, isActive) => {
    try {
      const endpoint = isActive
        ? `${baseUrl}/game-plan/deactivate/${id}`
        : `${baseUrl}/game-plan/activate/${id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      setGamePlans((prev) =>
        prev.map((g) =>
          (g.id === id || g._id === id) ? { ...g, isActive: !isActive } : g
        )
      );
      toast.success(`Game plan ${isActive ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      toast.error("Game plan activation/deactivation failed!");
    }
  };

  const columns = [
    {
      key: "game",
      header: "Game",
      render: (item) => {
        const selectedGame = games.find(game => (game._id || game.id) === item.gameId);
        return <span className="font-semibold">{selectedGame ? selectedGame.name : item.gameId}</span>;
      },
    },
    { key: "name", header: "Name" },
    { key: "coin", header: "Coin" },
    { key: "receiverCoin", header: "Receiver Coin" },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            item.isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
          }`}
        >
          {item.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => {
        const index = gamePlans.findIndex((g) => (g.id || g._id) === (item.id || item._id));
        return (
          <div className="flex flex-wrap gap-2">
            <button
              className="bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700 transition"
              onClick={() => handleEdit(index)}
            >
              Edit
            </button>
            <button
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
              onClick={() => handleDelete(item.id || item._id)}
            >
              Delete
            </button>
            <button
              className={`px-3 py-1 rounded text-white transition ${
                item.isActive ? "bg-gray-600 hover:bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={() => toggleActive(item.id || item._id, item.isActive)}
            >
              {item.isActive ? "Deactivate" : "Activate"}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-6">
          <h2 className="text-3xl font-bold mb-6 text-white">Game Plan Management</h2>

          {/* Tabs */}
          <div className="mb-6 overflow-x-auto">
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg w-max">
              <button
                onClick={() => handleTabChange("all", null)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "all"
                    ? "bg-brand text-white"
                    : "text-gray-300 hover:text-white hover:bg-gray-700"
                }`}
              >
                All
              </button>
              {games.map((game) => (
                <button
                  key={game._id || game.id}
                  onClick={() => handleTabChange(game._id || game.id, game._id || game.id)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors whitespace-nowrap ${
                    activeTab === (game._id || game.id)
                      ? "bg-brand text-white"
                      : "text-gray-300 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  {game.name}
                </button>
              ))}
            </div>
          </div>

          {/* Add button */}
          <button
            onClick={() => {
              setEditingIndex(null);
              resetForm();
              setShowModal(true);
            }}
            className="mb-6 flex items-center gap-2 bg-brand text-white font-semibold px-4 py-2 rounded hover:bg-brand-dark transition"
          >
            <PlusIcon className="h-5 w-5" />
            Add Game Plan
          </button>

          <Modal
            isOpen={showModal}
            onClose={() => !submitting && setShowModal(false)}
            title={editingIndex !== null ? "Edit Game Plan" : "Add New Game Plan"}
            size="lg"
            footer={
              <>
                <button
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={editingIndex !== null ? handleSave : handleAddNew}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark transition disabled:opacity-50"
                >
                  {submitting && <Spinner size="sm" />}
                  <span>
                    {submitting
                      ? (editingIndex !== null ? "Saving..." : "Creating...")
                      : (editingIndex !== null ? "Save Changes" : "Add Game Plan")}
                  </span>
                </button>
              </>
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Game</label>
                <select
                  name="gameId"
                  value={formData.gameId}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                >
                  <option value="">Select a Game</option>
                  {games.map((game) => (
                    <option key={game._id || game.id} value={game._id || game.id}>
                      {game.name}
                    </option>
                  ))}
                </select>
                {formErrors.gameId && <p className="text-red-400 text-sm">{formErrors.gameId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Game Plan Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Game Plan Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.name && <p className="text-red-400 text-sm">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Coin Amount</label>
                <input
                  type="number"
                  name="coin"
                  placeholder="Coin Amount"
                  value={formData.coin}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.coin && <p className="text-red-400 text-sm">{formErrors.coin}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Receiver Coin Amount</label>
                <input
                  type="number"
                  name="receiverCoin"
                  placeholder="Receiver Coin Amount"
                  value={formData.receiverCoin}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.receiverCoin && <p className="text-red-400 text-sm">{formErrors.receiverCoin}</p>}
              </div>
            </div>
          </Modal>

          {error && <p className="text-red-400 mb-4">{error}</p>}

          <Table
            columns={columns}
            rows={gamePlans}
            rowKey={(item) => item.id || item._id}
            loading={loading}
            emptyMessage="No game plans found. Create your first game plan!"
          />

          {!loading && gamePlans.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-4 py-3 bg-gray-800 rounded-lg gap-3">
              <div className="text-sm text-gray-400">
                Showing <span className="font-medium text-white">
                  {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                </span> to{' '}
                <span className="font-medium text-white">
                  {Math.min(currentPage * itemsPerPage, totalItems)}
                </span>{' '}
                of <span className="font-medium text-white">{totalItems}</span> results
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamePlan;
